/* ==========================================================================
   Crumpaw · Signature Bowl 2.1 — Step 2 mobile behaviour
   Loaded only by sections/cp-sb21-product-hero.liquid.

   Three jobs, all mobile-only and all inert above 749px:

   1. Dock offset. The storefront header is position:sticky; top:0, and the
      announcement bar above it is not sticky, so it scrolls away. Rather
      than assume either case, this measures whatever is actually pinned at
      the top of the viewport and writes the total into --cpsb21-dock-top.
      An announcement bar that is later made sticky is picked up for free.

   2. Preview mirror. cp-signature-bowl.js caches node references at init and
      writes the live preview through them, so a clone made afterwards is
      never written to directly. The copy is instead kept in step with the
      real frame, and deliberately not by rebuilding it: re-cloning on every
      keystroke would recreate the <img> elements and make them re-decode.
      Attributes and text are synced in place, and the copy is only rebuilt
      when the structure itself changes.

      The copy is scrubbed of data-cpsb hooks and ids, because the shared
      script still runs querySelector('[data-cpsb-motif=...]') during render
      and an unscrubbed duplicate could answer those lookups first.

      Three things trigger a sync, because any one of them can miss: the
      MutationObserver, a change/input on the section's own controls, and a
      re-resolve of the frame in case the theme replaced the node outright.

   3. Band centring. The printed band's position depends on the preview
      padding and baseline settings, so it is measured rather than assumed:
      the copy is shifted so the name row lands in the middle of the crop.

   4. Lead price. The buy form's price sits below every option control, which
      on a stacked mobile column puts it several screens down. A copy is shown
      under the subheading, mirrored from that element directly so there is
      still only one place the number is worked out.

   No dependencies. Nothing here mutates the 2.0 hero, its CSS or its JS.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE = '(max-width: 749px)';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isHook(name) {
    return name.indexOf('data-cpsb') === 0 || name === 'id' || name === 'for';
  }

  /* ---- 1. how much is pinned above us ---------------------------------- */

  function pinnedHeight(root) {
    var selector = root.getAttribute('data-cpsb21-sticky-selector');
    if (!selector) return 0;

    var total = 0;
    var seen = [];
    selector.split(',').forEach(function (part) {
      var sel = part.trim();
      if (!sel) return;
      var nodes;
      try {
        nodes = document.querySelectorAll(sel);
      } catch (error) {
        return; /* a mistyped selector must never break the page */
      }
      Array.prototype.forEach.call(nodes, function (node) {
        if (seen.indexOf(node) !== -1) return;
        var position = window.getComputedStyle(node).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        var rect = node.getBoundingClientRect();
        if (rect.height <= 0) return;
        seen.push(node);
        total += rect.height;
      });
    });
    return total;
  }

  function measure(root) {
    root.style.setProperty('--cpsb21-dock-top', pinnedHeight(root).toFixed(1) + 'px');
  }

  /* ---- 2. keep the copy in step with the real preview -------------------- */

  function scrub(node) {
    if (node.nodeType !== 1) return;
    Array.prototype.slice.call(node.attributes).forEach(function (attr) {
      if (isHook(attr.name)) node.removeAttribute(attr.name);
    });
    node.removeAttribute('fetchpriority');
    Array.prototype.forEach.call(node.children, scrub);
  }

  function copyAttributes(from, to, isRoot) {
    var wanted = {};
    Array.prototype.forEach.call(from.attributes, function (attr) {
      if (isHook(attr.name)) return;
      if (isRoot && attr.name === 'class') return; /* the copy carries its own */
      wanted[attr.name] = attr.value;
    });
    Array.prototype.slice.call(to.attributes).forEach(function (attr) {
      if (attr.name in wanted) return;
      if (attr.name === 'aria-hidden') return;
      if (isRoot && (attr.name === 'class' || attr.name === 'style')) return;
      to.removeAttribute(attr.name);
    });
    Object.keys(wanted).forEach(function (name) {
      if (to.getAttribute(name) !== wanted[name]) to.setAttribute(name, wanted[name]);
    });
  }

  /* Returns false when the shapes have diverged and a rebuild is needed. */
  function sync(from, to, isRoot) {
    if (from.tagName !== to.tagName) return false;
    if (from.children.length !== to.children.length) return false;
    copyAttributes(from, to, isRoot);
    if (!from.children.length) {
      if (to.textContent !== from.textContent) to.textContent = from.textContent;
      return true;
    }
    for (var i = 0; i < from.children.length; i++) {
      if (!sync(from.children[i], to.children[i], false)) return false;
    }
    return true;
  }

  function build(frame, into, className) {
    var copy = frame.cloneNode(true);
    scrub(copy);
    copy.classList.add(className);
    copy.setAttribute('aria-hidden', 'true');
    into.replaceChildren(copy);
    return copy;
  }

  /* The band's position depends on the preview padding and baseline
     settings, so measure where the name actually sits and centre on it
     rather than trusting a percentage. */
  function centreBand(crop, copy) {
    var row = copy.querySelector('.cpsb-preview__row--main');
    if (!row || !crop.clientHeight) return;
    var offset = row.offsetTop;
    if (!offset) return;
    copy.style.transform = 'translateY(' + (crop.clientHeight / 2 - offset).toFixed(1) + 'px)';
  }

  function Mirror(root) {
    this.root = root;
    this.dock = root.querySelector('[data-cpsb21="dock"]');
    this.crop = root.querySelector('[data-cpsb21="dock-crop"]');
    this.dialog = root.querySelector('[data-cpsb21="full"]');
    this.fullBody = root.querySelector('[data-cpsb21="full-body"]');
    this.fullConfig = root.querySelector('[data-cpsb21="full-config"]');
    this.copy = null;
    this.fullCopy = null;
    this.frame = null;
    this.observer = null;
    this.queued = false;
  }

  Mirror.prototype.frameNode = function () {
    /* Re-resolved every pass: the theme may replace the node outright, and a
       stale reference would silently freeze the copy. */
    var frame = this.root.querySelector('[data-cpsb="preview-frame"]');
    if (frame !== this.frame) {
      this.frame = frame;
      this.copy = null;
      this.watch();
    }
    return frame;
  };

  Mirror.prototype.watch = function () {
    if (this.observer) this.observer.disconnect();
    if (!this.frame || !window.MutationObserver) return;
    var self = this;
    this.observer = new MutationObserver(function () { self.schedule(); });
    this.observer.observe(this.frame, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true
    });
  };

  Mirror.prototype.schedule = function () {
    if (this.queued) return;
    this.queued = true;
    var self = this;
    window.requestAnimationFrame(function () {
      self.queued = false;
      self.paint();
    });
  };

  Mirror.prototype.paint = function () {
    var frame = this.frameNode();
    if (!frame) return;

    if (this.crop) {
      if (!this.copy || !this.copy.isConnected || !sync(frame, this.copy, true)) {
        this.copy = build(frame, this.crop, 'cpsb21-dock__inner');
      }
      centreBand(this.crop, this.copy);
      if (this.dock) this.dock.setAttribute('data-cpsb21-ready', 'on');
    }

    /* The dialog copy is only worth keeping current while it is on screen. */
    if (this.fullBody && this.isOpen()) {
      if (!this.fullCopy || !this.fullCopy.isConnected || !sync(frame, this.fullCopy, true)) {
        this.fullCopy = build(frame, this.fullBody, 'cpsb21-full__inner');
      }
      if (this.fullConfig) {
        var config = frame.querySelector('.cpsb-preview__config');
        this.fullConfig.textContent = config ? config.textContent.trim() : '';
      }
    }
  };

  Mirror.prototype.isOpen = function () {
    return Boolean(this.dialog && this.dialog.open);
  };

  Mirror.prototype.open = function (trigger) {
    if (!this.dialog || this.isOpen()) return;
    this.opener = trigger || null;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', ''); /* very old Safari */
    }
    this.paint();
  };

  Mirror.prototype.close = function () {
    if (!this.dialog || !this.isOpen()) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  };

  Mirror.prototype.start = function () {
    if (!this.dock || !this.crop) return false;
    this.paint();
    var self = this;
    /* Watch the section for the frame being replaced wholesale rather than
       mutated. The observer above is bound to a node that would be detached
       by that, and the copy would silently freeze on its last state.
       Mutations inside the dock are our own writes and are ignored, so this
       cannot feed itself. */
    if (window.MutationObserver) {
      this.structure = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (!self.dock.contains(records[i].target)) {
            self.schedule();
            return;
          }
        }
      });
      this.structure.observe(this.root, { childList: true, subtree: true });
    }
    /* Second trigger: the section's own controls. Runs after the theme's own
       handler on the same event, so the copy picks up whatever it rendered. */
    ['change', 'input'].forEach(function (type) {
      self.root.addEventListener(type, function () { self.schedule(); }, true);
    });
    /* Third: late-arriving images change the geometry the band was centred on. */
    window.addEventListener('load', function () { self.schedule(); }, { once: true });

    /* Both the Enlarge link and the full-preview button open the same dialog;
       the ✕ and Keep editing buttons close it. Esc is handled by <dialog>. */
    this.root.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-cpsb21="open-full"]');
      if (opener) {
        event.preventDefault();
        self.open(opener);
        return;
      }
      if (event.target.closest('[data-cpsb21="close-full"]')) {
        event.preventDefault();
        self.close();
      }
    });

    if (this.dialog) {
      /* Native dialogs do not restore focus themselves. */
      this.dialog.addEventListener('close', function () {
        if (self.opener && self.opener.isConnected) self.opener.focus();
        self.opener = null;
      });
      /* Clicking the backdrop lands on the dialog itself, not the card. */
      this.dialog.addEventListener('click', function (event) {
        if (event.target === self.dialog) self.close();
      });
    }
    return true;
  };

  Mirror.prototype.stop = function () {
    if (this.observer) this.observer.disconnect();
    if (this.structure) this.structure.disconnect();
    this.observer = null;
    this.structure = null;
  };

  /* ---- 3. a chosen print colour survives a bowl colour change ------------
     cp-signature-bowl.js clears the ink override whenever the colourway
     changes — "Matches the design: changing colourway resets the ink
     override". That file is shared with four templates, so rather than edit
     it, the choice is put back afterwards: remember the checked ink before
     the reset, re-select it after, and let the shared handler re-render
     through its own code path.

     Only a deliberate choice is restored. A customer who has never touched
     the print colour still gets each colourway's default, which is the point
     of the original behaviour — a pale band starts on dark ink. */

  function keepInk(root) {
    if (root.getAttribute('data-cpsb21-keep-ink') !== 'on') return;

    var chosen = false;
    var remembered = null;

    var checkedInk = function () {
      return root.querySelector('[data-cpsb="ink-input"]:checked');
    };

    var isSwatch = function (target) {
      return target && target.closest && target.closest('[data-cpsb="swatch"]');
    };

    var isColour = function (target) {
      return target && target.matches && target.matches('[data-cpsb="colour-input"]');
    };

    /* Capture runs before the control's own handler, so this still sees the
       ink that was selected before the reset. */
    root.addEventListener('change', function (event) {
      if (event.target && event.target.matches
        && event.target.matches('[data-cpsb="ink-input"]')) {
        chosen = true;
        return;
      }
      if (isColour(event.target)) remembered = chosen ? checkedInk() : null;
    }, true);

    /* The swatch strip switches colour on click without firing a change. */
    root.addEventListener('click', function (event) {
      if (chosen && isSwatch(event.target)) remembered = checkedInk();
    }, true);

    var restore = function () {
      var ink = remembered;
      remembered = null;
      if (!ink || !ink.isConnected || ink.checked) return;
      ink.checked = true;
      ink.dispatchEvent(new Event('change', { bubbles: true }));
    };

    /* Bubbling reaches the section after the control's own handler has run
       and re-rendered, so by here the reset has already happened. */
    root.addEventListener('change', function (event) {
      if (isColour(event.target)) restore();
    });
    root.addEventListener('click', function (event) {
      if (isSwatch(event.target)) restore();
    });
  }

  /* ---- 4. bring the chosen chip into view, once ------------------------- */

  function centreSelected(row) {
    var checked = row.querySelector('input:checked');
    if (!checked || !checked.id) return;
    var label;
    try {
      label = row.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id) + '"]');
    } catch (error) {
      return;
    }
    if (!label) return;
    /* Set scrollLeft directly. scrollIntoView would scroll the page as well,
       which would move Step 2 out from under the customer. */
    var target = label.offsetLeft - (row.clientWidth - label.offsetWidth) / 2;
    row.scrollLeft = Math.max(0, target);
  }

  function armRows(root) {
    var rows = root.querySelectorAll(
      '[data-cpsb21-row] .cpsb-inks, [data-cpsb21-row] .cpsb-fonts,' +
      ' [data-cpsb21-row] .cpsb-designs, [data-cpsb21-colour-row] .cpsb-chips');
    Array.prototype.forEach.call(rows, function (row) {
      if (row.dataset.cpsb21Touched === 'on') return;
      ['pointerdown', 'wheel', 'touchstart'].forEach(function (event) {
        row.addEventListener(event, function () {
          row.dataset.cpsb21Touched = 'on';
        }, { passive: true, once: true });
      });
      window.requestAnimationFrame(function () {
        if (row.dataset.cpsb21Touched !== 'on') centreSelected(row);
      });
    });
  }

  /* ---- wiring ----------------------------------------------------------- */

  function init(root) {
    if (root.dataset.cpsb21Init === 'on') return;
    root.dataset.cpsb21Init = 'on';

    var media = window.matchMedia(MOBILE);
    var mirror = null;

    /* Not viewport-dependent: the print colour should hold at any width. */
    keepInk(root);

    /* The docked strip, the Enlarge link and the full-preview button are
       mobile-only. They ship hidden so the desktop cascade never sees them,
       and are unhidden here rather than by a CSS rule outside the query. */
    var mobileOnly = root.querySelectorAll('[data-cpsb21-mobile-only]');
    var reveal = function (on) {
      Array.prototype.forEach.call(mobileOnly, function (el) { el.hidden = !on; });
    };

    var apply = function () {
      if (!media.matches) {
        reveal(false);
        if (mirror) {
          mirror.close();
          mirror.stop();
          mirror = null;
        }
        return;
      }
      reveal(true);
      measure(root);
      if (root.getAttribute('data-cpsb21-dock') === 'on' && !mirror) {
        mirror = new Mirror(root);
        if (!mirror.start()) mirror = null;
      }
      if (root.getAttribute('data-cpsb21-swipe') === 'on'
        || root.getAttribute('data-cpsb21-swipe-colours') === 'on') {
        armRows(root);
      }
    };

    apply();

    if (media.addEventListener) {
      media.addEventListener('change', apply);
    } else if (media.addListener) {
      media.addListener(apply);
    }

    var resize;
    window.addEventListener('resize', function () {
      window.clearTimeout(resize);
      resize = window.setTimeout(function () {
        if (!media.matches) return;
        measure(root);
        if (mirror) mirror.schedule();
      }, 120);
    }, { passive: true });

    /* The header can change height without the window resizing — a longer
       announcement string wrapping to two lines, or a logo swap. */
    if (window.ResizeObserver) {
      var selector = root.getAttribute('data-cpsb21-sticky-selector') || '';
      selector.split(',').forEach(function (part) {
        var sel = part.trim();
        if (!sel) return;
        var node;
        try {
          node = document.querySelector(sel);
        } catch (error) {
          return;
        }
        if (!node) return;
        new ResizeObserver(function () {
          if (media.matches) measure(root);
        }).observe(node);
      });
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.cpsb21'), init);
  }

  ready(boot);
  document.addEventListener('shopify:section:load', boot);
})();
